import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
  User,
  LogOut,
  Settings,
  Home,
  BookOpen,
  Target,
  FolderOpen,
  Menu,
  X,
  Bell,
  Sparkles,
  Brain,
  ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';


const Header = () => {
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);


  // 🔥 EMERGENCY SAFE USER EXTRACTION - PREVENTS OBJECT RENDERING
  const safeGetUserName = () => {
    try {
      if (!user) return 'User';
      if (typeof user === 'string') return user;
      if (typeof user === 'object') {
        // Handle different user object structures
        return user.name || user.username || user.firstName || user.displayName || 'User';
      }
      return 'User';
    } catch (e) {
      console.error('🔥 User name extraction error:', e);
      return 'User';
    }
  };


  const safeGetUserEmail = () => {
    try {
      if (!user) return 'user@example.com';
      if (typeof user === 'object') {
        return user.email || user.emailAddress || user.mail || 'user@example.com';
      }
      return 'user@example.com';
    } catch (e) {
      console.error('🔥 User email extraction error:', e);
      return 'user@example.com';
    }
  };


  const safeGetFirstName = () => {
    try {
      const fullName = safeGetUserName();
      const firstName = fullName.split(' ')[0];
      return firstName || 'User';
    } catch (e) {
      console.error('🔥 First name extraction error:', e);
      return 'User';
    }
  };


  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const handleLogout = async () => {
    try {
      console.log('🚪 User initiated logout');
      
      const loadingToast = toast.loading('Signing out...', {
        style: {
          background: '#1e293b',
          color: '#fff',
          border: '1px solid #374151'
        }
      });


      setIsUserMenuOpen(false);
      setIsMobileMenuOpen(false);


      await logout();


      toast.success('Signed out successfully! 👋', {
        id: loadingToast,
        duration: 2000,
        style: {
          background: '#059669',
          color: '#fff'
        }
      });


      navigate('/');


    } catch (error) {
      console.error('❌ Logout failed:', error);
      toast.error('Logout failed. Please try again.', {
        style: {
          background: '#dc2626',
          color: '#fff'
        }
      });
    }
  };


  const isActive = (path) => location.pathname === path;


  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsUserMenuOpen(false);
  };


  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };


  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setIsMobileMenuOpen(false);
  };


  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };


  // Show loading state during auth initialization
  if (isLoading) {
    return (
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Brain className="w-8 h-8 text-purple-400" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                AIVIONLearn
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-slate-700 rounded-lg animate-pulse"></div>
              <div className="w-20 h-4 bg-slate-700 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </motion.header>
    );
  }


  // Public header for non-authenticated users
  if (!isAuthenticated) {
    return (
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="flex items-center"
              >
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="w-8 h-8 text-purple-400 mr-2" />
                </motion.div>
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  AIVIONLearn
                </span>
              </motion.div>
            </Link>
            
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Sign In
                </motion.button>
              </Link>
              <Link to="/signup">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(139, 92, 246, 0.4)" }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium"
                >
                  Get Started
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>
    );
  }


  const navItems = [
    { to: '/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/roadmap', icon: BookOpen, label: 'Roadmap' },
    { to: '/projects', icon: Target, label: 'Projects' },
    { to: '/portfolio', icon: FolderOpen, label: 'Portfolio' }
  ];


  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-slate-900/95 backdrop-blur-xl shadow-2xl border-b border-slate-700/50' 
            : 'bg-slate-900/80 backdrop-blur-xl border-b border-slate-800'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center space-x-2">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ duration: 0.2 }}
                className="flex items-center"
              >
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <Brain className="w-8 h-8 text-purple-400 mr-2" />
                </motion.div>
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  AIVIONLearn
                </span>
              </motion.div>
            </Link>


            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-2">
              {navItems.map((item) => (
                <Link key={item.to} to={item.to}>
                  <motion.div
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative px-6 py-3 rounded-xl transition-all duration-300 ${
                      isActive(item.to)
                        ? 'bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30'
                        : 'hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <item.icon className={`w-5 h-5 ${
                          isActive(item.to) ? 'text-purple-400' : 'text-gray-400'
                        }`} />
                      </motion.div>
                      <span className={`font-medium ${
                        isActive(item.to) ? 'text-white' : 'text-gray-300'
                      }`}>
                        {item.label}
                      </span>
                    </div>
                    
                    {isActive(item.to) && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-400/30"
                      />
                    )}
                  </motion.div>
                </Link>
              ))}
            </nav>


            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              {/* Notifications */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative p-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-gray-400 hover:text-white transition-all duration-300"
              >
                <Bell className="w-5 h-5" />
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
                />
              </motion.button>


              {/* Enhanced User Menu */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-3 p-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-all duration-300"
                >
                  <div className="relative">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900"
                    />
                  </div>
                  <div className="hidden md:flex items-center space-x-2">
                    {/* 🔥 SAFE STRING RENDERING - LINE 881 FIX */}
                    <span className="text-white font-medium text-sm max-w-32 truncate">
                      {safeGetFirstName()}
                    </span>
                    <ChevronDown 
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                        isUserMenuOpen ? 'rotate-180' : ''
                      }`} 
                    />
                  </div>
                </motion.button>


                {/* Enhanced Dropdown Menu */}
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full mt-2 w-56 bg-slate-800/95 backdrop-blur-xl rounded-xl border border-slate-700/50 shadow-2xl py-2"
                    >
                      {/* User Info - SAFE RENDERING */}
                      <div className="px-4 py-3 border-b border-slate-700/50">
                        <p className="text-sm font-medium text-white truncate">
                          {safeGetUserName()}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {safeGetUserEmail()}
                        </p>
                      </div>


                      {/* Menu Items */}
                      <div className="py-2">
                        <Link to="/settings" onClick={closeUserMenu}>
                          <motion.div
                            whileHover={{ scale: 1.02, x: 5 }}
                            className="flex items-center space-x-3 px-4 py-3 hover:bg-slate-700/50 transition-all duration-300"
                          >
                            <Settings className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300">Settings</span>
                          </motion.div>
                        </Link>
                        
                        <motion.button
                          whileHover={{ scale: 1.02, x: 5 }}
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-500/10 hover:text-red-400 transition-all duration-300 text-gray-300"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Sign Out</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>


              {/* Mobile Menu Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleMobileMenu}
                className="md:hidden p-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-gray-400 hover:text-white transition-all duration-300"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </motion.button>
            </div>
          </div>


          {/* Enhanced Mobile Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden border-t border-slate-700/50 mt-4"
              >
                <div className="py-4 space-y-2">
                  {/* Navigation Items */}
                  {navItems.map((item, index) => (
                    <Link key={item.to} to={item.to} onClick={closeMobileMenu}>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileTap={{ scale: 0.95 }}
                        className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${
                          isActive(item.to)
                            ? 'bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30'
                            : 'hover:bg-slate-800/50'
                        }`}
                      >
                        <item.icon className={`w-5 h-5 ${
                          isActive(item.to) ? 'text-purple-400' : 'text-gray-400'
                        }`} />
                        <span className={`font-medium ${
                          isActive(item.to) ? 'text-white' : 'text-gray-300'
                        }`}>
                          {item.label}
                        </span>
                      </motion.div>
                    </Link>
                  ))}


                  {/* Mobile User Section - SAFE RENDERING */}
                  <div className="border-t border-slate-700/50 pt-4 mt-4">
                    <div className="flex items-center space-x-3 px-3 py-2 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          {safeGetUserName()}
                        </p>
                        <p className="text-xs text-gray-400">
                          {safeGetUserEmail()}
                        </p>
                      </div>
                    </div>


                    <Link to="/settings" onClick={closeMobileMenu}>
                      <motion.div
                        whileHover={{ x: 5 }}
                        className="flex items-center space-x-3 p-3 hover:bg-slate-800/50 rounded-lg transition-all duration-300"
                      >
                        <Settings className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-300">Settings</span>
                      </motion.div>
                    </Link>


                    <motion.button
                      whileHover={{ x: 5 }}
                      onClick={() => {
                        closeMobileMenu();
                        handleLogout();
                      }}
                      className="flex items-center space-x-3 p-3 hover:bg-red-500/10 hover:text-red-400 rounded-lg w-full text-left transition-all duration-300 text-gray-300"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>


      {/* Overlay */}
      {(isUserMenuOpen || isMobileMenuOpen) && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
          onClick={() => {
            closeUserMenu();
            closeMobileMenu();
          }}
        />
      )}
    </>
  );
};


export default Header;
